import { Arn, MatchingRuleInfo } from '../../utils/types'
import { TreeNodeWithTooltip } from './tree-node/tree-node.component'
import { intersectionSets } from './utils'
import { UserSelectableResource } from './app-resources-selector/app-resources-selector.component'

export interface ResourceNodeContext {
  selectedResources: Array<Arn>
  suppressedResources: Array<Arn>
  sourceResourcesByArn: Map<string, UserSelectableResource>
}

export class ResourceTreeNode implements TreeNodeWithTooltip<UserSelectableResource> {
  public parent: ResourceTreeNode = null
  public expanded = false
  public children: ResourceTreeNode[] = []
  public level = 0
  private _tooltipDataCache: { sections: any[]; simpleText: string } = null
  private _lastTooltipState: string = null
  constructor(
    public resource: UserSelectableResource,
    private context: ResourceNodeContext
  ) {}

  get key(): string {
    return this.resource.resourceArn
  }

  get sortKey(): string {
    return this.parent ? this.resource.displayNameAsChild : this.resource.displayName
  }

  get label(): string {
    let displayName = this.parent ? this.resource.displayNameAsChild : this.resource.displayName

    if (this.hasDescendants) {
      displayName += ` (${this.numSelectedDescendants}/${this.resource.descendantCount})`
    } else if (this.hasDirectDependees) {
      displayName += ` (${this.numSelectedDirectDependees}/${this.resource.directDependees.length})`
    }

    return displayName
  }

  get loading(): boolean {
    return this.resource.isContainer && this.resource.descendantCount > 0 && this.children.length == 0
  }

  get leaf(): boolean {
    return !this.resource.descendantCount
  }

  get draggable(): boolean {
    return false
  }

  get isArnSelected() {
    return this.resource.arnMatch
  }

  /**
   * Whether this resource is directly selected via a rule (tag, resource type, or resource group).
   */
  get isRuleSelected() {
    const hasMatchingRules = this.resource.matchingRules.size > 0
    return hasMatchingRules
  }

  /**
   * Whether this resource is directly selected via an ARN or rule.
   */
  get isDirectlySelected() {
    return this.isRuleSelected || this.isArnSelected
  }

  get isAncestorDirectlySelected() {
    let parent = this.parent
    while (parent !== null) {
      if (parent.isDirectlySelected) {
        return true
      }
      parent = parent.parent
    }
    return false
  }

  /**
   * Whether this resource is a hard dependency of another resource.
   */
  get isStrongDependeeSelected() {
    return this.resource.dependeeOf.size > 0
  }

  /**
   * Whether this resource is a soft dependency of another resource.
   */
  get isWeakDependeeSelected() {
    return this.resource.weakDependeeOf.size > 0
  }

  /**
   * Whether this resource is currently a weak dependency (selected or suppressed).
   */
  get isWeakDependency() {
    return this.resource.weakDependeeOf.size > 0 || this.context.suppressedResources.includes(this.resource.resourceArn)
  }

  /**
   * Whether this resource is transitively selected via a strong ot weak dependency and is not suppressed.
   */
  get isTransitivelySelected() {
    return (
      this.isStrongDependeeSelected ||
      (this.isWeakDependeeSelected && !this.context.suppressedResources.includes(this.resource.resourceArn))
    )
  }

  /**
   * Whether this resource has direct dependees (a.k.a. children).
   */
  get hasDirectDependees() {
    return this.resource.directDependees.length > 0
  }

  /**
   * Whether all direct dependees of this resource are directly or transitively selected.
   */
  get areAllDirectDependeesSelected() {
    if (this.hasDirectDependees) {
      return this.numSelectedDirectDependees === this.resource.directDependees.length
    }

    return false
  }

  get areSomeDirectDependeesSelected() {
    if (this.hasDirectDependees) {
      const numSelected = this.numSelectedDirectDependees
      return numSelected > 0 && numSelected < this.resource.directDependees.length
    }

    return false
  }

  get hasDescendants() {
    return this.children.length > 0 || this.resource.descendantCount > 0
  }

  get areAllDescendantsSelected() {
    if (!this.hasDescendants) {
      return false
    }
    const totalDescendants = this.children.length > 0 ? this.children.length : this.resource.descendantCount
    return this.numSelectedDescendants === totalDescendants
  }

  get areSomeDescendantsSelected() {
    if (this.hasDescendants) {
      const numSelected = this.numSelectedDescendants
      const totalDescendants = this.resource.descendantCount
      const result = numSelected > 0 && numSelected < totalDescendants

      return result
    }

    return false
  }

  get numSelectedDescendants() {
    if (this.isDirectlySelected || this.isAncestorDirectlySelected) {
      return this.resource.descendantCount
    }

    let count = 0
    for (let child of this.children) {
      if (child.isDirectlySelected || child.isTransitivelySelected) {
        count++
      }
      count += child.numSelectedDescendants
    }
    return count
  }

  /**
   * Return the number of direct dependees of this resource that are selected.
   */
  get numSelectedDirectDependees() {
    // using Sets is much faster than using arrays (Set.has has sublinear time complexity)
    let directDependeesSet = new Set(this.resource.directDependees)
    let directlySelectedDirectDependees = intersectionSets(new Set(this.context.selectedResources), directDependeesSet)
    let transitivelySelectedDirectDependees = intersectionSets(this.resource.dependeeOf, directDependeesSet)
    let transitivelySelectedWeakDependees = intersectionSets(this.resource.weakDependeeOf, directDependeesSet)
    let totalSelected = new Set([
      ...directlySelectedDirectDependees,
      ...transitivelySelectedDirectDependees,
      ...transitivelySelectedWeakDependees,
    ]).size
    return totalSelected
  }

  /**
   * Checkbox state reflects whether the resource is selected by ID, by a tag rule,
   * or transitively by another resource.  It's checked if it matches on ARN or tag,
   * if it is a strong dependee of anything else, or if it's a week dependee of something
   * else but it's not suppressed.
   */
  get checked() {
    return (
      this.isDirectlySelected ||
      (this.isTransitivelySelected && !this.hasDirectDependees) ||
      this.isAncestorDirectlySelected ||
      this.areAllDirectDependeesSelected ||
      this.areAllDescendantsSelected
    )
  }

  /**
   * Whether the checkbox for this resource should be disabled. A checkbox is disabled if it cannot change state when clicked.
   *
   * The logic is as follow:
   * Resources that are excluded by exclusion rules should be disabled and show an fa-x icon overlay.
   * Resources that are directly selected by a tag should be disabled since the selection is configured by the tag, not by the checkbox.
   * Resources that are selected by an ARN rule should not be disabled to allow the user to deselect it.
   * Resources that are weak dependees of another resource should not be disabled to allow the user to add a weak exclude rule.
   * Resources that are a strong dependee of another selected resource should be disabled.
   * Resources that have direct dependees and all of the direct dependees are selected should be disabled.
   */
  get checkboxDisabled() {
    if (this.isExcluded) {
      return true
    }

    if (this.isRuleSelected) {
      return true
    }

    // Evaluate ARN selection explicitly, before evaluate transitive selection, so that the user can unselect
    // a resource that is involved in a circular dependency and would otherwise be pulled in with a disabled
    // checkbox due to the isStrongDependeeSelected check.
    if (this.isArnSelected) {
      return false
    }

    if (this.isWeakDependeeSelected) {
      return false
    }

    if (this.isStrongDependeeSelected && !this.hasDirectDependees && !this.hasDescendants) {
      return true
    }

    if (this.isAncestorDirectlySelected) {
      return true
    }

    if (this.hasDirectDependees && this.areAllDirectDependeesSelected) {
      return true
    }

    if (this.hasDescendants && this.areAllDescendantsSelected) {
      return true
    }

    return false
  }

  get selectable() {
    return !this.checkboxDisabled
  }

  /**
   * Whether this resource is excluded due to exclusion rules.
   */
  get isExcluded() {
    return this.resource.isExcluded || false
  }

  /**
   * Whether this resource has dependee resources that could be copied.
   */
  get hasDependeeResourceValues() {
    const resource = this.resource
    const allDependees = new Set([...resource.dependeeOf, ...resource.weakDependeeOf])
    return allDependees.size > 0 && Array.from(allDependees).some((arn) => arn !== resource.resourceArn)
  }

  /**
   * Get the list of dependee ARNs for clipboard copying.
   */
  get dependeeArns() {
    const resource = this.resource
    const allDependees = new Set([...resource.dependeeOf, ...resource.weakDependeeOf])
    return Array.from(allDependees)
      .filter((arn) => arn !== resource.resourceArn)
      .sort((a, b) => a.localeCompare(b))
  }

  /**
   * Whether some (but not all) children are affected by exclusion rules.
   */
  get hasSomeExcludedChildren() {
    if (!this.hasDescendants && !this.hasDirectDependees) {
      return false
    }

    // Check descendants (children in tree structure)
    if (this.hasDescendants) {
      const excludedDescendants = this.children.filter((child) => child.isExcluded).length
      if (excludedDescendants > 0 && excludedDescendants < this.children.length) {
        return true
      }
    }

    // Check direct dependees
    if (this.hasDirectDependees) {
      const excludedDependees = this.resource.directDependees.filter((arn) => {
        const dependee = this.context.sourceResourcesByArn.get(arn)
        return dependee?.isExcluded || false
      }).length
      if (excludedDependees > 0 && excludedDependees < this.resource.directDependees.length) {
        return true
      }
    }

    return false
  }

  /**
   * Mark the checkbox as "indeterminate" if the resource is not directly selected has some descendants or direct
   * dependees selected, or if any children are affected by exclusion rules.
   */
  get partialSelected() {
    const result =
      !this.isDirectlySelected &&
      (this.areSomeDescendantsSelected || this.areSomeDirectDependeesSelected || this.hasSomeExcludedChildren)

    return result
  }

  /**
   * Whether a tooltip should be displayed for this resource.
   */
  get shouldDisplayTooltip() {
    return this.isExcluded || this.isWeakDependency || this.isRuleSelected || this.isTransitivelySelected
  }

  get tooltip(): string {
    // For backwards compatibility with simple tooltip display
    if (this.tooltipData) {
      return this.tooltipData.simpleText
    }
    return ''
  }

  get tooltipData() {
    // Check if the checkbox should display tooltip information.
    if (this.shouldDisplayTooltip) {
      const resource = this.resource
      const sections = []

      // Create a state key to detect when we need to recalculate
      const stateKey = `${this.isExcluded}-${this.checked}-${resource.matchingRules.size}-${resource.resourceType || 'none'}-${Array.from(
        resource.matchingRules.entries()
      )
        .map(([k, v]) => `${k}:${v.value}:${v.operator}`)
        .join(',')}`

      // Return cached result if state hasn't changed
      if (this._lastTooltipState === stateKey && this._tooltipDataCache) {
        return this._tooltipDataCache
      }

      // Check if this resource is excluded by exclusion rules
      if (this.isExcluded) {
        sections.push({
          type: 'exclusion',
          text: 'This resource is <strong>disabled</strong> because it matches an <strong>exclusion rule</strong>. Resources that match exclusion rules cannot be selected, even if they would otherwise be included by tag or ARN rules.',
        })
      }

      // Check if this resource is a weak dependency, but suppress if it's also a strong dependency
      if (this.isWeakDependency && !this.isStrongDependeeSelected) {
        const statusText =
          this.isWeakDependency && !this.checked
            ? 'This <strong>weak dependency</strong> has been <strong>unchecked</strong> and will not be included in your application.'
            : 'This resource is a <strong>weak dependency</strong> of another selected resource.'
        const explanationText =
          ' Weak dependencies are optional resources that may be needed by your application but can be safely excluded if not required.'

        sections.push({
          type: 'weakDependency',
          text: statusText + explanationText,
        })
      }

      if (resource.matchingRules.size || resource.resourceType) {
        const matchingRules = []

        // Process tag matches from matchingRules
        Array.from(resource.matchingRules.entries()).forEach(([key, ruleInfo]) => {
          const operatorText = ruleInfo.operator === 'is_not' ? 'is not' : 'is'
          if (ruleInfo.value === null) {
            matchingRules.push(`<strong>${key}</strong> ${operatorText} <strong><i>Any value</i></strong>`)
          } else {
            if (key.includes('resourceType')) {
              matchingRules.push(`<strong>Resource Type</strong> ${operatorText} <strong>${ruleInfo.value}</strong>`)
            } else {
              matchingRules.push(`<strong>${key}</strong> ${operatorText} <strong>${ruleInfo.value}</strong><br>`)
            }
          }
        })

        // Handle tag rules display
        if (matchingRules.length > 0) {
          const ruleDisplay = matchingRules.join('')

          sections.push({
            type: resource.tags && resource.tags.size > 0 ? 'tags' : 'resourceType',
            text: `${ruleDisplay}`,
          })
        }
      }

      // Handle dependee information
      const allDependees = new Set([...resource.dependeeOf, ...resource.weakDependeeOf])
      if (allDependees.size) {
        const dependeeDetails = Array.from(allDependees)
          .filter((arn) => arn !== resource.resourceArn)
          .sort((a, b) => a.localeCompare(b))
          .map((arn) => {
            const resourceInfo = this.getParentResourceTypeAndNameFunction(arn)
            return {
              arn: arn,
              displayText: resourceInfo,
            }
          })

        if (dependeeDetails.length > 0) {
          const displayLimit = 5
          const visibleDependees = dependeeDetails.slice(0, displayLimit)
          const hasMore = dependeeDetails.length > displayLimit

          sections.push({
            type: 'dependees',
            text: 'This resource is depended upon by:',
            dependeeResources: visibleDependees,
            moreCount: hasMore ? dependeeDetails.length - displayLimit : 0,
            allDependeeArns: dependeeDetails.map((d) => d.arn),
          })
        }
      }

      // Create simple text version for backwards compatibility
      const simpleText = sections
        .map((section) => {
          if (section.type === 'dependees') {
            const dependeeDisplay = section.dependeeResources.map((d) => d.displayText).join('\n')
            const moreText = section.moreCount > 0 ? `\n...${section.moreCount} more resources` : ''
            return `${section.text} \n\n${dependeeDisplay}${moreText}`
          }
          return section.text
        })
        .join('\n')

      // Cache the result
      this._tooltipDataCache = { sections, simpleText: simpleText || 'No resources depend on this.' }
      this._lastTooltipState = stateKey

      return {
        sections,
        simpleText: simpleText || 'No resources depend on this.',
      }
    }

    return null
  }

  /**
   * This function gets each dependent resource object's 'displayName'
   * and 'resourceType' properties using the (arnHoveredUpon: string)
   * @param arnHoveredUpon
   * @returns
   */
  getParentResourceTypeAndNameFunction(arnHoveredUpon: string): string {
    let dependeeDetails: string[] = []
    let resource = this.context.sourceResourcesByArn.get(arnHoveredUpon)
    // If the resource is a child (e.g. API Gateway Stage), resolve to its parent container
    // (e.g. API Gateway REST API) for a more meaningful tooltip display.
    if (resource?.parentArn) {
      const parentResource = this.context.sourceResourcesByArn.get(resource.parentArn)
      if (parentResource) {
        resource = parentResource
      }
    }
    if (resource) {
      // Get each dependent resource object 'displayName' and 'resourceType' properties
      const name = resource.displayName
      const type = resource.resourceTypeDetails.displayName // Ensures singular form of the resource type
      dependeeDetails.push(`<tr><td>${type}</td><td>&nbsp;&nbsp;</td><td><strong>${name}</strong></td></tr>`)
    }

    // Return formatted HTML or a fallback message
    return dependeeDetails.length > 0 ? dependeeDetails.join('') : arnHoveredUpon
  }
}

export class K8sClusterResourcesTreeNode extends ResourceTreeNode {
  get key(): string {
    return `${this.resource.resourceArn}-root`
  }

  get sortKey(): string {
    return 'Cluster-Scoped Resources'
  }

  get label(): string {
    return `Cluster-Scoped Resources (${this.numSelectedDescendants}/${this.children.length})`
  }

  get leaf(): boolean {
    return this.children.length == 0
  }

  get selectable(): boolean {
    return false
  }

  get checked(): boolean {
    return this.isAncestorDirectlySelected || this.areAllDescendantsSelected
  }

  get isDirectlySelected(): boolean {
    return false
  }

  get isTransitivelySelected(): boolean {
    return false
  }

  get partialSelected(): boolean {
    return this.areSomeDescendantsSelected || this.hasSomeExcludedChildren
  }

  get areAllDescendantsSelected() {
    return this.children.length > 0 && this.numSelectedDescendants === this.children.length
  }

  get areSomeDescendantsSelected() {
    if (this.children.length > 0) {
      const numSelected = this.numSelectedDescendants
      return numSelected > 0 && numSelected < this.children.length
    }

    return false
  }

  get numSelectedDescendants() {
    let count = 0
    for (let child of this.children) {
      if (child.isDirectlySelected || child.isTransitivelySelected) {
        count++
      }
      count += child.numSelectedDescendants
    }
    return count
  }

  get shouldDisplayTooltip(): boolean {
    return false
  }
}
